import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import React, { useCallback, useMemo, useRef } from 'react';

import { ColumnDef, SortModel } from './ServiceTable.types';
import { ServiceTableHead } from './ServiceTableHead';
import { ServiceTableRow } from './ServiceTableRow';
import { elasticLastColumnSx, stickyActionsTableSx } from '../../../configs/tableStyles';
import { useFreezeColumnWidths } from '../../../hooks/useFreezeColumnWidths';

// Fixed widths of the non-data columns, used to size the table when frozen.
const CHECKBOX_COL_WIDTH = 48;
const ACTIONS_COL_WIDTH = 70;

export interface ServiceTableProps {
  data: Record<string, unknown>[];
  columns: ColumnDef[];
  sortModel: SortModel;
  onSortChange: (model: SortModel) => void;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onPreview: (id: number) => void;
  onRowAction: (id: number, action: string) => void;
  onColumnReorder?: (oldIndex: number, newIndex: number) => void;
  onColumnResize?: (key: string, width: number, seed?: Record<string, number>) => void;
  loading?: boolean;
  /** When false, Location Name / Primary Allocation render as plain text
   *  instead of structure-page hyperlinks. Restricted users (clients,
   *  partners) lacking Structure_Edit_View_Structure_Location see read-only
   *  values rather than links that would 404 or hit a permission error. */
  canViewStructure?: boolean;
  /** When true, the selection column (checkboxes + Select-all) and the
   *  per-row actions column (eye preview + 3-dot menu) are not rendered.
   *  Client/customer users (visibility rule with hasClientCustomer) see a
   *  read-only list without actions they couldn't perform anyway. */
  isClientCustomer?: boolean;
}

export const ServiceTable: React.FC<ServiceTableProps> = ({
  data,
  columns,
  sortModel,
  onSortChange,
  selectedIds,
  onSelectionChange,
  onPreview,
  onRowAction,
  onColumnReorder,
  onColumnResize,
  loading,
  canViewStructure = false,
  isClientCustomer = false,
}) => {
  const allIds = useMemo(() => data.map((r) => r.serviceId as number).filter(Boolean), [data]);
  const visibleColumns = useMemo(() => columns.filter((c) => c.visible), [columns]);

  // When every visible column has an explicit width the table renders as a
  // fixed-pixel colgroup, so resizing one column never disturbs the others and
  // the table scrolls horizontally instead of squeezing.
  const isFrozen = visibleColumns.length > 0 && visibleColumns.every((c) => c.width != null);
  const frozenTableWidth = useMemo(() => {
    if (!isFrozen) return undefined;
    const dataWidth = visibleColumns.reduce((sum, c) => sum + (c.width ?? 0), 0);
    return dataWidth + (isClientCustomer ? 0 : CHECKBOX_COL_WIDTH + ACTIONS_COL_WIDTH);
  }, [isFrozen, visibleColumns, isClientCustomer]);

  // Seed every not-yet-sized column to its rendered content width immediately
  // after the first paint (before it's visible), so the table is ALWAYS in the
  // precise fixed-pixel model. Auto-layout can't shrink a column below its
  // content, which is what made dragging drift and columns un-shrinkable.
  const tableRef = useRef<HTMLTableElement>(null);
  useFreezeColumnWidths({ tableRef, isFrozen, ready: data.length > 0, onColumnResize });
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = allIds.some((id) => selectedIds.includes(id));

  const handleSort = useCallback(
    (field: string) => {
      if (sortModel.field !== field) {
        // New column: start with asc
        onSortChange({ field, direction: 'asc' });
      } else if (sortModel.direction === 'asc') {
        // asc → desc
        onSortChange({ field, direction: 'desc' });
      } else {
        // desc → off (revert to default)
        onSortChange({ field: 'serviceId', direction: 'desc' });
      }
    },
    [sortModel, onSortChange]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      onSelectionChange(checked ? allIds : []);
    },
    [allIds, onSelectionChange]
  );

  const toggleSelection = useCallback(
    (id: number) => {
      const next = selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id];
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange]
  );

  const handleShiftClick = useCallback(
    (index: number) => {
      const lastSelectedIdx = data.findIndex(
        (r) => r.serviceId === selectedIds[selectedIds.length - 1]
      );
      if (lastSelectedIdx === -1) {
        const id = data[index]?.serviceId as number;
        if (id) onSelectionChange([...selectedIds, id]);
        return;
      }
      const start = Math.min(lastSelectedIdx, index);
      const end = Math.max(lastSelectedIdx, index);
      const rangeIds = data
        .slice(start, end + 1)
        .map((r) => r.serviceId as number)
        .filter(Boolean);
      const merged = Array.from(new Set([...selectedIds, ...rangeIds]));
      onSelectionChange(merged);
    },
    [data, selectedIds, onSelectionChange]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={32} sx={{ color: 'v4.accent' }} />
        </Box>
      )}
      <TableContainer>
        <Table
          ref={tableRef}
          size="small"
          aria-label="Service table"
          data-testid="service-table"
          sx={
            isFrozen
              ? {
                  tableLayout: 'fixed',
                  width: frozenTableWidth,
                  // The frozen width is the sum of the measured column widths,
                  // which is routinely narrower than the container — that gap is
                  // the white band beside the sticky actions column. Floor the
                  // table at the container width; the elastic last data column in
                  // the colgroup below absorbs the slack, so every other column
                  // keeps the exact width it was frozen at.
                  minWidth: '100%',
                  ...stickyActionsTableSx,
                  ...elasticLastColumnSx,
                }
              : // Size columns to their content (fit the longest cell) rather than
                // stretching to fill — the same content-based sizing the report
                // view uses, and the exact widths the first resize freezes to
                // (so nothing shifts on first grab). Wider-than-viewport tables
                // scroll horizontally.
                //
                // `minWidth: 100%` keeps the table from ending short of its
                // container when the content is narrow, which otherwise leaves a
                // white band down the right-hand side beside the sticky actions
                // column. It also gives `elasticLastColumnSx` the slack it needs
                // — its stretch of the last data column is what absorbs the gap.
                {
                  width: 'max-content',
                  minWidth: '100%',
                  ...stickyActionsTableSx,
                  ...elasticLastColumnSx,
                }
          }
        >
          {isFrozen && (
            <colgroup>
              {!isClientCustomer && <col style={{ width: CHECKBOX_COL_WIDTH }} />}
              {visibleColumns.map((c, i) => (
                <col
                  key={c.key}
                  // Last data column is left elastic (no width) so it takes up
                  // any slack when the frozen width is under the container
                  // width. Under `table-layout: fixed` a column with no width
                  // absorbs the remainder; giving every column a width would
                  // instead stretch them all proportionally and undo the
                  // widths the user dragged.
                  style={i === visibleColumns.length - 1 ? undefined : { width: c.width }}
                />
              ))}
              {!isClientCustomer && <col style={{ width: ACTIONS_COL_WIDTH }} />}
            </colgroup>
          )}
          <ServiceTableHead
            columns={visibleColumns}
            sortModel={sortModel}
            onSort={handleSort}
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={handleSelectAll}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            // The last data column is elastic while frozen, so it has no width
            // of its own to drag — suppress its resize handle.
            elasticLastColumn={isFrozen}
            isClientCustomer={isClientCustomer}
          />
          <TableBody>
            {data.map((row, idx) => {
              const id = row.serviceId as number;
              return (
                <ServiceTableRow
                  key={id ?? idx}
                  row={row}
                  columns={visibleColumns}
                  selected={selectedIds.includes(id)}
                  even={idx % 2 === 1}
                  canViewStructure={canViewStructure}
                  isClientCustomer={isClientCustomer}
                  onClick={() => toggleSelection(id)}
                  onShiftClick={() => handleShiftClick(idx)}
                  onPreview={() => onPreview(id)}
                  onAction={(action) => onRowAction(id, action)}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
